import React, { useEffect, useState } from 'react';
import { adminDb } from '../../services/adminService';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const when = (value) => value ? new Date(value).toLocaleString() : '—';

const ActionConfirmation = ({ action, reason, setReason, confirmationCode, setConfirmationCode, busy, perform, cancel }) => (
  <div role="group" aria-label="Confirm administrative action"
    className="mt-4 rounded-xl border-2 border-amber-500 bg-amber-50 p-5">
    <h3 className="font-semibold">Confirm {action.kind === 'stripe' ? 'Stripe recheck for' : action.kind === 'invitation' ? 'invitation resend to' : action.kind === 'hold' ? 'read-only access for' : action.kind === 'restore' ? 'restored write access for' : action.kind === 'delete' ? 'expedited deletion of' : action.activate ? 'reactivation of' : 'deactivation of'} {action.label}</h3>
    {action.kind === 'hold' && <p className="mt-2 text-sm">Parents, Guardians, and Kids can still sign in and view family data. All family changes stop immediately. No deletion is scheduled. An administrator can restore writes later; subscription access rules still apply.</p>}
    {action.kind === 'restore' && <p className="mt-2 text-sm">This removes the administrator hold. Family writes resume only if the current entitlement permits them.</p>}
    {action.kind === 'delete' && <p className="mt-2 text-sm font-medium">The family becomes read-only immediately. It becomes eligible for the next daily purge at 03:00 UTC; this button does not purge now. The Family Organizer cannot cancel this request. The purge removes family data and queues Kid login cleanup. Internal audit and support history remain. Active or unended Stripe subscriptions block the request and the purge.</p>}
    {action.kind === 'delete' && <label className="mt-3 block text-sm">Type family code {action.familyCode} to confirm
      <input className="mt-1 w-full rounded border p-2" value={confirmationCode}
        onChange={(event) => setConfirmationCode(event.target.value)} autoComplete="off" />
    </label>}
    <label className="mt-3 block text-sm">Administrator reason
      <textarea autoFocus className="mt-1 w-full rounded border p-2" value={reason} maxLength={500}
        onChange={(event) => setReason(event.target.value)} />
    </label>
    <div className="mt-3 flex gap-2">
      <button type="button" disabled={busy || reason.trim().length < (action.kind === 'delete' ? 10 : 5) ||
        (action.kind === 'delete' && confirmationCode !== action.familyCode)}
        className="rounded bg-amber-800 px-3 py-2 text-white disabled:opacity-50" onClick={perform}>Confirm action</button>
      <button type="button" disabled={busy} onClick={cancel}>Cancel</button>
    </div>
  </div>
);

export default function AdminMvpPanels({ overview, onChanged }) {
  const { adminContext } = useAdminAuth();
  const familyId = overview.family.family_id;
  const [notes, setNotes] = useState([]);
  const [audit, setAudit] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingHistoryError, setBillingHistoryError] = useState('');
  const [billingProviderFilter, setBillingProviderFilter] = useState('all');
  const [billingStatusFilter, setBillingStatusFilter] = useState('all');
  const [auditAction, setAuditAction] = useState('');
  const [auditAdmin, setAuditAdmin] = useState('');
  const [auditFrom, setAuditFrom] = useState('');
  const [auditTo, setAuditTo] = useState('');
  const [note, setNote] = useState('');
  const [correctsNoteId, setCorrectsNoteId] = useState(null);
  const [category, setCategory] = useState('general');
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const refresh = async () => {
    const [nextNotes, nextAudit] = await Promise.all([
      adminDb.listNotes(familyId), adminDb.listAudit(familyId),
    ]);
    setNotes(nextNotes);
    setAudit(nextAudit);
    adminDb.listBillingHistory(familyId)
      .then((rows) => { setBillingHistory(rows); setBillingHistoryError(''); })
      .catch(() => setBillingHistoryError('Billing history could not be loaded.'));
  };

  useEffect(() => {
    let current = true;
    Promise.all([adminDb.listNotes(familyId), adminDb.listAudit(familyId)])
      .then(([nextNotes, nextAudit]) => {
        if (current) { setNotes(nextNotes); setAudit(nextAudit); }
      })
      .catch(() => { if (current) setError('Support history could not be loaded.'); });
    adminDb.listBillingHistory(familyId)
      .then((rows) => { if (current) { setBillingHistory(rows); setBillingHistoryError(''); } })
      .catch(() => { if (current) setBillingHistoryError('Billing history could not be loaded.'); });
    return () => { current = false; };
  }, [familyId]);

  const perform = async () => {
    if (!action || reason.trim().length < (action.kind === 'delete' ? 10 : 5) ||
      (action.kind === 'delete' && confirmationCode !== action.familyCode)) return;
    setBusy(true); setError(''); setMessage('');
    try {
      if (action.kind === 'stripe') {
        const result = await adminDb.recheckStripe(familyId, reason.trim());
        setMessage(result.differences.length
          ? `Stripe recheck found ${result.differences.length} difference(s): ${result.differences.map((item) => item.field).join(', ')}. Entitlement was not changed.`
          : 'Stripe recheck found no status, price, or cancellation difference.');
      } else if (action.kind === 'invitation') {
        await adminDb.resendInvitation(familyId, action.invitation_id, reason.trim());
        setMessage('Guardian invitation email sent.');
      } else if (action.kind === 'hold' || action.kind === 'restore') {
        const result = await adminDb.setFamilyReadOnly(familyId, action.kind === 'hold',
          reason.trim(), crypto.randomUUID());
        if (!result.ok) throw new Error(result.error || 'Family status could not be changed.');
        setMessage(action.kind === 'hold' ? 'Family is now read-only.' : 'Administrator hold removed.');
      } else if (action.kind === 'delete') {
        const result = await adminDb.scheduleExpeditedDeletion(familyId,
          reason.trim(), crypto.randomUUID());
        if (!result.ok) throw new Error(result.error || 'Deletion could not be scheduled.');
        setMessage('Family is read-only and eligible for the next daily purge.');
      } else {
        const result = await adminDb.changeChild(familyId, action.child_id,
          action.activate, reason.trim(), crypto.randomUUID());
        if (!result.ok) throw new Error(result.error || 'Action failed');
        setMessage(result.replayed ? 'Already completed.' : 'Child status updated.');
      }
      await Promise.all([refresh(), onChanged()]);
      setAction(null); setReason(''); setConfirmationCode('');
    } catch (cause) {
      setError(cause.message || 'Action failed. Please retry after checking the family.');
      await refresh().catch(() => {});
    } finally { setBusy(false); }
  };

  const addNote = async (event) => {
    event.preventDefault();
    if (!note.trim()) return;
    setBusy(true); setError(''); setMessage('');
    try {
      await adminDb.addNote(familyId, note.trim(), category,
        correctsNoteId ? 'Correction to internal support note' : 'Internal support note',
        crypto.randomUUID(), correctsNoteId);
      setNote(''); setCorrectsNoteId(null); setMessage('Internal note saved.');
      await refresh();
    } catch {
      setError('The note could not be saved. Please check and try again.');
    } finally { setBusy(false); }
  };

  return <div className="space-y-6">
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="font-semibold">Billing and lifecycle</h3>
      <p className="mt-1 text-xs text-slate-500">Supabase entitlement state. Stripe recheck reads current provider status and records differences without changing billing state.</p>
      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
        <div><dt className="text-slate-500">Family time zone</dt><dd>{overview.time_zone || '—'}</dd></div>
        <div><dt className="text-slate-500">Provider status</dt><dd>{overview.billing?.provider_status || '—'}</dd></div>
        <div><dt className="text-slate-500">Cancellation scheduled</dt><dd>{overview.billing?.cancel_at_period_end ? 'Yes' : 'No'}</dd></div>
        <div><dt className="text-slate-500">Cancellation effective</dt><dd>{when(overview.billing?.cancellation_effective_at)}</dd></div>
        <div><dt className="text-slate-500">Scheduled change</dt><dd>{[overview.billing?.pending_plan, overview.billing?.pending_billing_interval].filter(Boolean).join(' / ') || 'None'}</dd></div>
        <div><dt className="text-slate-500">Change effective</dt><dd>{when(overview.billing?.pending_change_effective_at)}</dd></div>
        <div><dt className="text-slate-500">Last provider event</dt><dd>{when(overview.billing?.last_provider_event_at)}</dd></div>
        <div><dt className="text-slate-500">Deletion scheduled</dt><dd>{when(overview.family.deletion_scheduled_for)}</dd></div>
      </dl>
      {overview.entitlement?.stored?.billing_provider === 'stripe' &&
        overview.entitlement?.stored?.provider_subscription_id &&
        <button type="button" className="mt-4 rounded border border-blue-600 px-3 py-2 text-blue-700"
          onClick={() => { setAction({ kind: 'stripe', label: overview.family.display_name || 'this family' }); setReason(''); }}>
          Recheck Stripe
        </button>}
      {action?.kind === 'stripe' && <ActionConfirmation action={action} reason={reason} setReason={setReason}
        busy={busy} perform={perform} cancel={() => setAction(null)} />}
    </section>
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="font-semibold">Billing history (18-month retention)</h3>
      <p className="mt-1 text-xs text-slate-500">Verified provider event metadata. This is not a revenue ledger; test renewals are not real charges.</p>
      {billingHistoryError && <p role="alert" className="mt-3 text-sm text-amber-700">{billingHistoryError}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <label className="text-xs text-slate-600">Provider
          <select className="ml-2 rounded border p-1 text-sm" value={billingProviderFilter}
            onChange={(e) => setBillingProviderFilter(e.target.value)}>
            <option value="all">All</option><option value="stripe">Stripe</option>
            <option value="google_play">Google</option><option value="apple">Apple</option>
            <option value="entitlement">Entitlement changes</option>
          </select>
        </label>
        <label className="text-xs text-slate-600">Result
          <select className="ml-2 rounded border p-1 text-sm" value={billingStatusFilter}
            onChange={(e) => setBillingStatusFilter(e.target.value)}>
            <option value="all">All</option><option value="verified">Verified</option>
            <option value="processed">Processed</option><option value="failed">Failed</option>
            <option value="grace">Grace</option><option value="active">Active</option>
            <option value="expired">Expired</option>
          </select>
        </label>
      </div>
      {billingHistory.length ? (
        <div className="mt-3 max-h-96 overflow-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b"><th className="py-2">Date</th><th>Provider</th><th>Event</th><th>Status</th><th>Mode</th></tr></thead>
            <tbody>{billingHistory.filter((item) =>
              (billingProviderFilter === 'all' || item.source === billingProviderFilter) &&
              (billingStatusFilter === 'all' || item.status === billingStatusFilter)
            ).map((item, index) => (
              <tr key={index} className="border-b border-slate-100">
                <td className="py-2">{when(item.occurred_at || item.recorded_at)}</td>
                <td>{item.source}</td><td>{item.event_type}</td>
                <td>{item.status}</td><td>{item.environment}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : <p className="mt-3 text-sm text-slate-500">No recorded billing events for this family yet.</p>}
    </section>
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="font-semibold">Guardian invitations</h3>
      {overview.invitations?.length
        ? <ul className="mt-3 divide-y text-sm">{overview.invitations.map((item) =>
          <li className="flex items-center justify-between gap-2 py-2" key={item.id}>
            <span>{item.email} · {item.status} · Expires {when(item.expires_at)}</span>
            {item.status === 'pending' && <button type="button" className="rounded border px-3 py-1 text-blue-700"
              onClick={() => { setAction({ kind: 'invitation', invitation_id: item.id, label: item.email }); setReason(''); }}>Resend</button>}
          </li>)}</ul>
        : <p className="mt-2 text-sm text-slate-500">No invitations.</p>}
      {action?.kind === 'invitation' && <ActionConfirmation action={action} reason={reason} setReason={setReason}
        busy={busy} perform={perform} cancel={() => setAction(null)} />}
    </section>
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="font-semibold">Child status actions</h3>
      <p className="mt-1 text-xs text-slate-500">Financial history and login identity are preserved. Reactivation follows the family child limit.</p>
      <ul className="mt-3 divide-y text-sm">
        {overview.children.map((child) => <li className="flex items-center justify-between gap-3 py-2" key={child.child_id}>
          <span>{child.nickname || child.first_name} · {child.status}</span>
          <button type="button" className="rounded border px-3 py-1 text-blue-700"
            onClick={() => { setAction({ kind: 'child', child_id: child.child_id, activate: child.status !== 'active',
              label: child.nickname || child.first_name }); setReason(''); }}
          >{child.status === 'active' ? 'Deactivate' : 'Reactivate'}</button>
        </li>)}
      </ul>
      {action?.kind === 'child' && <ActionConfirmation action={action} reason={reason} setReason={setReason}
        busy={busy} perform={perform} cancel={() => setAction(null)} />}
    </section>
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="font-semibold">Internal support notes</h3>
      <p className="mt-1 text-xs text-slate-500">Visible only to administrators. Do not enter PINs, passwords, payment details, or sensitive child information.</p>
      {correctsNoteId && <p className="mt-2 text-sm text-amber-800">Adding a correction to note {correctsNoteId}. The original remains visible.</p>}
      <form className="mt-3 space-y-2" onSubmit={addNote}>
        <select className="rounded border p-2" value={category} onChange={(event) => setCategory(event.target.value)}>
          {['general','billing','access','lifecycle'].map((item) => <option key={item}>{item}</option>)}
        </select>
        <textarea className="w-full rounded border p-2" value={note} maxLength={2000}
          onChange={(event) => setNote(event.target.value)} aria-label="Internal support note" />
        <button className="rounded bg-slate-900 px-3 py-2 text-white disabled:opacity-50"
          disabled={busy || !note.trim()}>{correctsNoteId ? 'Add correction' : 'Add note'}</button>
        {correctsNoteId && <button type="button" onClick={() => setCorrectsNoteId(null)}>Cancel correction</button>}
      </form>
      {notes.length ? <ul className="mt-4 divide-y text-sm">{notes.map((item) =>
        <li key={item.id} className="py-3"><div className="text-slate-500">{item.author} · {item.category} · {when(item.created_at)}</div>
          <p className="whitespace-pre-wrap">{item.note}</p>
          {item.corrects_note_id && <p className="text-xs text-amber-700">Corrects note {item.corrects_note_id}</p>}
          <button className="mt-1 text-xs text-blue-700" type="button" onClick={() => { setCorrectsNoteId(item.id); setNote(''); }}>Add correction</button>
        </li>)}</ul>
        : <p className="mt-3 text-sm text-slate-500">No internal notes yet.</p>}
    </section>
    <section className="rounded-xl border-2 border-red-300 bg-white p-5">
      <h3 className="font-semibold text-red-900">Danger Zone</h3>
      <p className="mt-1 text-sm text-slate-600">These actions require a reason and are recorded in immutable audit history. Open an action to review its effect before confirming.</p>
      <div className="mt-4 space-y-4 text-sm">
        <div>
          <p className="font-medium">Family access</p>
          <p className="mt-1 text-slate-600">An administrator hold stops family writes immediately without changing Stripe billing or scheduling deletion.</p>
          {overview.family.status === 'active' && <button type="button" disabled={busy}
            className="mt-2 rounded border border-amber-600 px-3 py-2 text-amber-800"
            onClick={() => { setAction({ kind: 'hold', label: overview.family.display_name || overview.family.kid_family_code }); setReason(''); }}>
            Make family read-only
          </button>}
          {overview.family.status === 'admin_read_only' && <button type="button" disabled={busy}
            className="mt-2 rounded border px-3 py-2 text-blue-700"
            onClick={() => { setAction({ kind: 'restore', label: overview.family.display_name || overview.family.kid_family_code }); setReason(''); }}>
            Restore family writes
          </button>}
          {overview.family.status === 'inactive' && <p className="mt-2 text-amber-800">Deletion is already scheduled. The family is read-only.</p>}
          {(action?.kind === 'hold' || action?.kind === 'restore') && <ActionConfirmation action={action}
            reason={reason} setReason={setReason} busy={busy} perform={perform} cancel={() => setAction(null)} />}
        </div>
        {adminContext?.admin_role === 'super_admin' && <div className="border-t pt-4">
          <p className="font-medium text-red-900">Expedited deletion · Super Admin only</p>
          <p className="mt-1 text-slate-600">Schedule an eligible family for the next daily purge. Resolve any linked Stripe subscription through the approved billing support process first. Billing portal access is unavailable during an administrator hold. This does not cancel billing or delete records when clicked.</p>
          {overview.family.status !== 'inactive' && <button type="button" disabled={busy}
            className="mt-2 rounded bg-red-700 px-3 py-2 font-medium text-white disabled:opacity-50"
            onClick={() => { setAction({ kind: 'delete', label: overview.family.display_name || overview.family.kid_family_code,
              familyCode: overview.family.kid_family_code }); setReason(''); setConfirmationCode(''); }}>
            Schedule expedited deletion
          </button>}
          {action?.kind === 'delete' && <ActionConfirmation action={action} reason={reason}
            setReason={setReason} confirmationCode={confirmationCode} setConfirmationCode={setConfirmationCode}
            busy={busy} perform={perform} cancel={() => setAction(null)} />}
        </div>}
      </div>
    </section>
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="font-semibold">Audit history</h3>
      <form className="mt-3 flex flex-wrap gap-2 text-sm" onSubmit={async (event) => {
        event.preventDefault(); setError('');
        try { setAudit(await adminDb.listAudit(familyId, { action: auditAction,
          administratorId: auditAdmin, from: auditFrom || null,
          to: auditTo || null })); }
        catch { setError('Audit history could not be filtered. Check the administrator ID and dates.'); }
      }}>
        <input className="rounded border p-2" aria-label="Audit action" placeholder="Action" value={auditAction} onChange={(event) => setAuditAction(event.target.value)} />
        <input className="rounded border p-2" aria-label="Administrator ID" placeholder="Administrator UUID" value={auditAdmin} onChange={(event) => setAuditAdmin(event.target.value)} />
        <input className="rounded border p-2" aria-label="From date" type="date" value={auditFrom} onChange={(event) => setAuditFrom(event.target.value)} />
        <input className="rounded border p-2" aria-label="Before date" type="date" value={auditTo} onChange={(event) => setAuditTo(event.target.value)} />
        <button className="rounded border px-3 py-2">Filter</button>
      </form>
      {audit.length ? <ul className="mt-3 divide-y text-sm">{audit.map((item) =>
        <li className="py-2" key={item.id}>{when(item.created_at)} · {item.administrator_role} ({item.administrator_id}) · {item.action} · {item.outcome}
          {item.reason && <span> · {item.reason}</span>}</li>)}</ul>
        : <p className="mt-2 text-sm text-slate-500">No recorded activity.</p>}
    </section>
    {message && <p role="status" className="text-emerald-700">{message}</p>}
    {error && <p role="alert" className="text-red-700">{error}</p>}
  </div>;
}
